import { AlertCircle } from 'lucide-react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';

interface NoCarrierModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * R56-F3 — the backend's own refusal sentence, already localized, taken
   * from the failing response body. When present it REPLACES the generic
   * hint. Omit it and the generic copy still shows.
   */
  reason?: string;
  /**
   * BOXY-301 — the refusal's machine-readable code, when the backend sent one.
   *
   * Carried for context only; it does NOT gate what the Select branch shows.
   * Owner decision 2026-09-28: `reason` is already filtered upstream by
   * `carrierRefusalReason` (business-coded refusals only), and that is the
   * single filter for every branch.
   */
  reasonCode?: string;
  /**
   * 🔴 WHETHER THE MERCHANT ASKED FOR TEMPERATURE-CONTROLLED DELIVERY.
   *
   * The Select branch's fallback hint is UNCONDITIONAL — it is the only
   * sentence a Select merchant can receive when the backend answers 200 with
   * an empty carrier list, and it fires for every no-carrier outcome: wrong
   * size, unserved lane, carrier outage, cold. So a remedy hard-coded into it
   * is wrong for most of them, which is the same defect as the "check the
   * delivery address" line we removed today — advice to change the one thing
   * that is not the problem.
   *
   * 🔴 AND ONE OF THOSE REMEDIES IS UNSAFE TO GIVE. "Try without temperature
   * control" tells someone shipping food or medicine to drop the cold chain so
   * a validation screen will let them through. The platform must not nudge
   * anyone into spoiling goods to place an order. Caught by BOXY::QA.
   */
  coldRequested?: boolean;
  /**
   * BOXY-501 — the refusal named a service that CAN carry this order
   * (MULTI_BOX_NEEDS_OTHER_SERVICE). One click switches the form to it.
   */
  switchService?: { title: string; onSwitch: () => void };
}

const NoCarrierModal = ({
  open,
  onClose,
  reason,
  coldRequested,
  switchService,
}: NoCarrierModalProps) => {
  const { t } = useTranslation();
  const switchButton = switchService ? (
    <button
      onClick={() => {
        switchService.onSwitch();
        onClose();
      }}
      className='px-5 py-2 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
    >
      {String(t('use_service', { service: switchService.title }))}
    </button>
  ) : null;
  const gotItClass = switchService
    ? 'px-5 py-2 rounded-lg border border-solid border-sd-border bg-white text-sd-foreground text-sm font-semibold hover:bg-sd-muted transition-colors'
    : 'px-5 py-2 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity';

  // To every merchant Boxy IS the service (owner 2026-10-28: no carrier on
  // any merchant screen), so: no carrier vocabulary, no rule vocabulary, and
  // no "go to carrier settings" CTA. The failure still has to be SAID though —
  // suppressing the whole modal is what made the NEXT button look dead.
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
      className='sd-scope sd-font'
    >
      <div className='flex flex-col items-center justify-center text-center gap-5 py-6 px-2'>
        <div className='h-12 w-12 rounded-full bg-sd-muted flex items-center justify-center'>
          <AlertCircle className='h-6 w-6 text-sd-muted-foreground' strokeWidth={1.5} />
        </div>
        <div className='space-y-2 max-w-sm'>
          <p className='text-sm font-medium text-sd-foreground'>
            {String(t('boxy_select_no_service_title'))}
          </p>
          {/* 🔴 F-DASH-2 — THE SELECT BRANCH USED TO DROP THE BACKEND'S REASON.
                The generic branch has honoured `reason` since R56-F3; this one
                still printed a fixed hint telling the merchant to "check the
                delivery address and try again". Measured on the real refusal:
                the API answers 760001 "No carrier can deliver this shipment.
                Try a different parcel size or destination" — which NAMES the
                remedy — and the merchant was sent to re-check an address that
                was never wrong. A Select merchant is the one who meets this
                modal most, because Boxy IS their carrier: they have no carrier
                toggles to go and fix, so the sentence is all they get. */}
          <p className='text-xs text-sd-muted-foreground'>
            {reason
              ? reason
              : String(
                  t(
                    coldRequested
                      ? 'boxy_select_no_service_hint_cold'
                      : 'boxy_select_no_service_hint',
                  ),
                )}
          </p>
          {reason ? (
            <p className='text-xs text-sd-muted-foreground'>
              {String(
                t(
                  'order_data_preserved_simple',
                  'Nothing was saved yet — your details are still on the form.',
                ),
              )}
            </p>
          ) : null}
        </div>
        <div className='flex gap-3'>
          {switchButton}
          <button onClick={onClose} className={gotItClass}>
            {String(t('got_it'))}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default NoCarrierModal;
